import { AlertTriangle, Loader2 } from 'lucide-react';

import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';

import { POSTPONE_HOURS } from './constants';

interface PostponeButtonState {
  disabled: boolean;
  title?: string;
  statusHint: string | null;
}

interface PostponeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  incrementalLockAllowed: boolean;
  postponePreviewTime: string;
  postponeCountMax: number;
  postponeCountUsed: number;
  postponeModalError: string;
  isPostponePending: boolean;
  postponeButtonState: PostponeButtonState;
  onConfirmPostpone: () => void;
}

export function PostponeDialog({
  open,
  onOpenChange,
  incrementalLockAllowed,
  postponePreviewTime,
  postponeCountMax,
  postponeCountUsed,
  postponeModalError,
  isPostponePending,
  postponeButtonState,
  onConfirmPostpone,
}: PostponeDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='gap-0 overflow-hidden p-0 sm:max-w-md'>
        <DialogHeader className='space-y-0 border-b border-border px-6 py-4 text-left'>
          <DialogTitle className='text-lg font-semibold text-foreground'>
            Postpone Payment Run?
          </DialogTitle>
        </DialogHeader>
        <div className='space-y-4 px-6 py-4'>
          {incrementalLockAllowed ? (
            <>
              <p className='text-sm leading-relaxed text-foreground'>
                This sets a later time for the payment run window. Charges you
                already <strong>approved for this lock</strong> are not
                delayed—they still run on the normal automatic schedule. Other
                charges stay out of the run until you add them or unlock.
              </p>
              <p className='text-sm text-muted-foreground'>
                Postpone window ends:{' '}
                <span className='font-semibold text-foreground'>
                  {postponePreviewTime}
                </span>
              </p>
            </>
          ) : (
            <p className='text-sm leading-relaxed text-foreground'>
              This will push your payment run to{' '}
              <span className='font-semibold'>{postponePreviewTime}</span>. Your
              families will not be charged until you approve or the new auto-run
              time arrives.
            </p>
          )}
          <div className='flex gap-3 rounded-lg bg-muted/60 p-3 text-sm text-muted-foreground'>
            <AlertTriangle
              className='mt-0.5 size-5 shrink-0 text-amber-600'
              aria-hidden
            />
            <p>
              You can postpone up to {postponeCountMax} times (
              {postponeCountMax * POSTPONE_HOURS} hours maximum). After that,
              you&apos;ll need to approve or manually reschedule.
            </p>
          </div>
          <p className='text-sm text-foreground'>
            Postponements used:{' '}
            <span className='font-semibold'>
              {postponeCountUsed} of {postponeCountMax}
            </span>
          </p>
          {postponeModalError ? (
            <p className='text-sm text-destructive' role='alert'>
              {postponeModalError}
            </p>
          ) : null}
        </div>
        <DialogFooter className='flex-row justify-between gap-3 border-t border-border bg-muted/20 px-6 py-4 sm:justify-between'>
          <Button
            type='button'
            variant='outline'
            className='border-neutral-300 bg-background'
            onClick={() => {
              onOpenChange(false);
            }}
            disabled={isPostponePending}
          >
            Cancel
          </Button>
          <Button
            type='button'
            className='bg-amber-500 font-semibold text-white hover:bg-amber-600'
            onClick={onConfirmPostpone}
            disabled={
              isPostponePending || postponeButtonState.disabled
            }
          >
            {isPostponePending ? (
              <>
                <Loader2 className='mr-2 size-4 animate-spin' aria-hidden />
                Postponing…
              </>
            ) : (
              'Confirm Postpone'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
